import { useState } from 'react'
import type { SummaryInput } from '@creanizer/types'
import { Label } from '@creanizer/ui'
import { Textarea } from '@creanizer/ui'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@creanizer/ui'
import { RadioGroup, RadioGroupItem } from '@creanizer/ui'
import { Slider } from '@creanizer/ui'
import { Checkbox } from '@creanizer/ui'
import { FileUpload } from './FileUpload'
import { TextInput } from './TextInput'
import { SummarySettings } from './SummarySettings'

interface SummaryFormProps {
  input: SummaryInput
  onChange: (input: SummaryInput) => void
  charCount: number
}

export function SummaryForm({ input, onChange, charCount }: SummaryFormProps) {
  const [activeInputTab, setActiveInputTab] = useState<'text' | 'file'>('text')
  
  const handleFileLoad = (content: string, fileName: string) => {
    onChange({
      ...input,
      inputType: 'file',
      fileContent: content,
      fileName
    })
    setActiveInputTab('file')
  }
  
  const handleTextChange = (text: string) => {
    onChange({
      ...input,
      inputType: 'text',
      text
    })
  }
  
  return (
    <div className="space-y-6">
      {/* 入力方法選択 */}
      <div className="space-y-4">
        <Label>入力方法</Label>
        <RadioGroup
          value={activeInputTab}
          onValueChange={(value) => setActiveInputTab(value as 'text' | 'file')}
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="text" id="text" />
            <Label htmlFor="text" className="cursor-pointer">
              テキスト直接入力
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="file" id="file" />
            <Label htmlFor="file" className="cursor-pointer">
              ファイルアップロード
            </Label>
          </div>
        </RadioGroup>
      </div>
      
      {/* 入力エリア */}
      <div className="space-y-4">
        {activeInputTab === 'text' ? (
          <TextInput
            value={input.text || ''}
            onChange={handleTextChange}
            charCount={charCount}
          />
        ) : (
          <FileUpload
            onFileLoad={handleFileLoad}
            fileName={input.fileName}
            fileContent={input.fileContent}
          />
        )}
      </div>
      
      {/* 要約設定 */}
      <div className="space-y-4 border-t pt-4">
        <h4 className="font-medium">要約設定</h4>
        
        {/* 目標文字数 */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="targetLength">目標文字数</Label>
            <span className="text-sm text-muted-foreground">
              {input.targetLength.toLocaleString()}文字
            </span>
          </div>
          <Slider
            id="targetLength"
            min={1000}
            max={20000}
            step={1000}
            value={[input.targetLength]}
            onValueChange={([value]) => onChange({ ...input, targetLength: value })}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>1,000</span>
            <span>5,000</span>
            <span>10,000</span>
            <span>15,000</span>
            <span>20,000</span>
          </div>
        </div>
        
        {/* 要約スタイル */}
        <div className="space-y-2">
          <Label htmlFor="summaryStyle">要約スタイル</Label>
          <Select
            value={input.summaryStyle}
            onValueChange={(value) => onChange({ 
              ...input, 
              summaryStyle: value as SummaryInput['summaryStyle'] 
            })}
          >
            <SelectTrigger id="summaryStyle">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="narrative">物語形式（時系列に沿った流れ）</SelectItem>
              <SelectItem value="bullet">箇条書き（要点を整理）</SelectItem>
              <SelectItem value="hybrid">ハイブリッド（物語＋箇条書き）</SelectItem>
              <SelectItem value="dialogue">対話中心（会話を抽出）</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        {/* テキストタイプ */}
        <div className="space-y-2">
          <Label htmlFor="textType">テキストタイプ</Label>
          <Select
            value={input.textType || 'auto'}
            onValueChange={(value) => onChange({ 
              ...input, 
              textType: value as SummaryInput['textType'] 
            })}
          >
            <SelectTrigger id="textType">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="auto">自動判定</SelectItem>
              <SelectItem value="novel">小説</SelectItem>
              <SelectItem value="trpg_log">TRPGログ</SelectItem>
              <SelectItem value="screenplay">脚本・シナリオ</SelectItem>
              <SelectItem value="document">ドキュメント・説明文</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        {/* 保持要素 */}
        <div className="space-y-2">
          <Label>保持する要素</Label>
          <div className="grid grid-cols-2 gap-4">
            <label className="flex items-center space-x-2">
              <Checkbox
                checked={input.preserveElements.dialogue}
                onCheckedChange={(checked) => onChange({
                  ...input,
                  preserveElements: { ...input.preserveElements, dialogue: checked === true }
                })}
              />
              <span className="text-sm">台詞</span>
            </label>
            <label className="flex items-center space-x-2">
              <Checkbox
                checked={input.preserveElements.actions}
                onCheckedChange={(checked) => onChange({
                  ...input,
                  preserveElements: { ...input.preserveElements, actions: checked === true }
                })}
              />
              <span className="text-sm">行動</span>
            </label>
            <label className="flex items-center space-x-2">
              <Checkbox
                checked={input.preserveElements.descriptions}
                onCheckedChange={(checked) => onChange({
                  ...input,
                  preserveElements: { ...input.preserveElements, descriptions: checked === true }
                })}
              />
              <span className="text-sm">描写</span>
            </label>
            <label className="flex items-center space-x-2">
              <Checkbox
                checked={input.preserveElements.emotions}
                onCheckedChange={(checked) => onChange({
                  ...input,
                  preserveElements: { ...input.preserveElements, emotions: checked === true }
                })}
              />
              <span className="text-sm">感情</span>
            </label>
            <label className="flex items-center space-x-2">
              <Checkbox
                checked={input.preserveElements.foreshadowing}
                onCheckedChange={(checked) => onChange({
                  ...input,
                  preserveElements: { ...input.preserveElements, foreshadowing: checked === true }
                })}
              />
              <span className="text-sm">伏線</span>
            </label>
            <label className="flex items-center space-x-2">
              <Checkbox
                checked={input.preserveElements.keyPoints}
                onCheckedChange={(checked) => onChange({
                  ...input,
                  preserveElements: { ...input.preserveElements, keyPoints: checked === true }
                })}
              />
              <span className="text-sm">重要ポイント</span>
            </label>
          </div>
        </div>
        
        {/* 詳細設定 */}
        <SummarySettings
          input={input}
          onChange={onChange}
        />
      </div>
    </div>
  )
}